import type { Track } from "@similarweb/tracking-provider";

import type { MenuItemOption } from "app/pages/sales-intelligence/common-components/options-menu/types";

import type {
    CrmEnrichmentJob,
    CrmEnrichmentJobObject,
    CrmEnrichmentJobRefreshPeriod,
    CrmEnrichmentJobTabName,
    CrmEnrichmentJobType,
    CrmEnrichmentSource,
    CrmEnrichmentWizardSteps,
    CrmProvider,
    LeadScoreRefreshDataInterval,
    Operator,
} from "../../types/types";

import {
    ENRICHMENT_SOURCE_TRACKING_NAME,
    JOB_INTERVAL_TRACKING_NAME,
    JOB_OBJECT_TRACKING_NAME,
    JOB_OPERATOR_TRACKING_NAME,
    JOB_TYPE_TRACKING_NAME,
    LEAD_SCORE_INTERVAL_TRACKING_NAME,
    WIZARD_STEP_TRACKING_NAME,
} from "./constants";

/**
 * Creates a unified CRM enrichment module tracking service
 */
export const createCrmEnrichmentTrackingService = (track: Track) => {
    const handleTrack = (crmProvider: CrmProvider, action: string, eventName: string) => {
        track(`${crmProvider} integration`, action, eventName);
    };

    return {
        // Job list operations
        trackCreateJobClick: (numberOfExistingJobs: number, crmProvider: CrmProvider) => {
            handleTrack(crmProvider, "create job", `new enrichment job/${numberOfExistingJobs}`);
        },

        trackToggleJobClick: (enabled: boolean, job: CrmEnrichmentJob, crmProvider: CrmProvider) => {
            const { type, enrichmentObject } = job;

            handleTrack(
                crmProvider,
                `${enabled ? "enable" : "disable"} enrichment job`,
                `turn enrichment ${enabled ? "on" : "off"}/${JOB_TYPE_TRACKING_NAME[type]}/${
                    JOB_OBJECT_TRACKING_NAME[enrichmentObject]
                }`,
            );
        },

        trackOpenJobOptions: (jobName: string, crmProvider: CrmProvider) => {
            handleTrack(crmProvider, "options menu", `more options/${jobName}`);
        },

        trackSelectJobOption: (optionName: MenuItemOption, crmProvider: CrmProvider) => {
            handleTrack(
                crmProvider,
                "select option",
                `more options/${optionName === "edit" ? "settings" : optionName}`,
            );
        },

        trackRenameJob: (newName: string, oldName: string, crmProvider: CrmProvider) => {
            handleTrack(crmProvider, "approve rename", `rename integration/${newName}/${oldName}`);
        },

        trackRemoveJob: (job: CrmEnrichmentJob, crmProvider: CrmProvider) => {
            const { type, enrichmentObject } = job;
            handleTrack(
                crmProvider,
                "delete enrichment job",
                `delete enrichment/${JOB_TYPE_TRACKING_NAME[type]}/${JOB_OBJECT_TRACKING_NAME[enrichmentObject]}`,
            );
        },

        trackLogsButtonClick: (crmProvider: CrmProvider) => {
            handleTrack(crmProvider, "select option", `more options/logs`);
        },

        trackDownloadLogs: (source: string) => {
            track("logs download", "download results", `logs results/${source}`);
        },

        trackLogsPagination: (page: number) => {
            track("pagination", "click", `table/page/${page}`);
        },

        // Wizard operations
        wizard: {
            trackClose: (page: CrmEnrichmentWizardSteps, crmProvider: CrmProvider) => {
                handleTrack(
                    crmProvider,
                    "close enrichment wizard",
                    `${crmProvider} enrichment/${WIZARD_STEP_TRACKING_NAME[page]}`,
                );
            },

            trackClickNext: (page: CrmEnrichmentWizardSteps, crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "next step", `${crmProvider} enrichment/${WIZARD_STEP_TRACKING_NAME[page]}`);
            },

            trackFinish: (name: string, crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "finish wizard", `new enrichment job/${name}`);
            },

            trackSelectType: (type: CrmEnrichmentJobType, crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "select enrichment type", `selected type/${JOB_TYPE_TRACKING_NAME[type]}`);
            },

            trackSelectEnrichmentSource: (source: CrmEnrichmentSource, crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "click", `enrichment level/${ENRICHMENT_SOURCE_TRACKING_NAME[source]}`);
            },

            trackSelectObject: (object: CrmEnrichmentJobObject, crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "select record", `change record type/${JOB_OBJECT_TRACKING_NAME[object]}`);
            },

            trackSelectCountry: (country: string, crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "select country filter", `change country/${country}`);
            },

            trackSelectRefreshInterval: (interval: CrmEnrichmentJobRefreshPeriod, crmProvider: CrmProvider) => {
                handleTrack(
                    crmProvider,
                    "select refresh frequency",
                    `change frequency/${JOB_INTERVAL_TRACKING_NAME[interval]}`,
                );
            },

            trackSelectLeadScoreRefreshInterval: (interval: LeadScoreRefreshDataInterval, crmProvider: CrmProvider) => {
                handleTrack(
                    crmProvider,
                    "select lead score refresh frequency",
                    `change lead score frequency/${LEAD_SCORE_INTERVAL_TRACKING_NAME[interval]}`,
                );
            },

            trackSelectSection: (
                sectionName: CrmEnrichmentJobTabName,
                crmProvider: CrmProvider,
                type?: CrmEnrichmentJobType,
            ) => {
                handleTrack(
                    crmProvider,
                    "select section",
                    `edit section/${sectionName === "filters" ? "rules" : sectionName}/${
                        type ? JOB_TYPE_TRACKING_NAME[type] : ""
                    }`,
                );
            },
        },

        // Filter operations
        filters: {
            trackAdd: (numberOfFilters: number, crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "add rule", `new enrichment rule/${numberOfFilters}`);
            },

            trackRemove: (numberOfFilters: number, crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "remove rule", `remove enrichment rule/${numberOfFilters}`);
            },

            trackSelectOperator: (operator: Operator, crmProvider: CrmProvider) => {
                handleTrack(
                    crmProvider,
                    "select parameter",
                    `enrichment rule parameter/${JOB_OPERATOR_TRACKING_NAME[operator]}`,
                );
            },

            trackSelectAllEntities: (crmProvider: CrmProvider) => {
                handleTrack(crmProvider, "enrich all records", "no enrichment rules");
            },
        },

        // Edit operations
        edit: {
            trackName: (newName: string, crmProvider: CrmProvider, oldName?: string) => {
                handleTrack(crmProvider, "edit name", `enrichment name/${newName}/${oldName || ""}`);
            },
        },
    };
};
